import React, { useState, useEffect } from 'react';
import { SectionTiles } from '../components/SectionTiles';
import { EnterForm } from '../components/EnterForm';
import { FormShortcutHints } from '../components/ui/form-shortcut-hints';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DateInput } from '../components/ui/date-input';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { formatDate, getToday, formatApiErrorDetail } from '../lib/utils';
import { Factory, FlaskConical, GitBranch, Plus, Trash2, AlertCircle, ChevronDown, ChevronUp, FileText, ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { ExportToolbar } from '../components/ui/export-toolbar';
import { usePageCallbacks } from '../hooks/usePageCallbacks';
import {
  getProducts, getProductions, createProduction, deleteProduction,
  getLabReports, createLabReport, updateLabReport, deleteLabReport,
  getBatchTimeline, getBatchTimelineProducts, getProductStockDetail,
} from '../lib/api';

const toTons = (kg) => kg ? `${(kg / 1000).toFixed(2)}` : '0.00';
const formatCurrency = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const TYPE_COLORS = {
  batch_created: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  production_consumed: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  sales_dispatch: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  lab_report: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
};

// Stable per-row uids so removing/reordering line items doesn't confuse React.
let _prodCounter = 0;
const _prodUid = () => `pi-${Date.now()}-${++_prodCounter}`;
const _emptyInput = () => ({ _uid: _prodUid(), product_id: '', batch_id: '', quantity_kg: '' });
const _emptyOutput = () => ({ _uid: _prodUid(), product_id: '', quantity_kg: '', brand: '' });

export default function Production() {
  const [activeTab, setActiveTab] = useState('');
  const [products, setProducts] = useState([]);

  // Production state
  const [productions, setProductions] = useState([]);
  const [prodLoading, setProdLoading] = useState(false);
  const [prodDialog, setProdDialog] = useState(false);
  const [prodForm, setProdForm] = useState({ date: getToday(), input_items: [_emptyInput()], output_items: [_emptyOutput()], remarks: '' });
  const [prodError, setProdError] = useState('');
  const [prodSubmitting, setProdSubmitting] = useState(false);
  const [availableBatches, setAvailableBatches] = useState({});
  const [expandedProdId, setExpandedProdId] = useState(null);

  // Lab Reports state
  const [labReports, setLabReports] = useState([]);
  const [labLoading, setLabLoading] = useState(false);
  const [labDialog, setLabDialog] = useState(false);
  const [labForm, setLabForm] = useState({ production_id: '', batch_id: '', date: getToday(), temperature: '', time: '', ratio: '', viscosity: '', composition: '', remarks: '' });
  const [labError, setLabError] = useState('');
  const [labSubmitting, setLabSubmitting] = useState(false);
  const [editingLab, setEditingLab] = useState(null);
  const [expandedLabId, setExpandedLabId] = useState(null);

  // Batch Timeline state
  const [allBatches, setAllBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [expandedTimelineIdx, setExpandedTimelineIdx] = useState(null);

  useEffect(() => {
    if (activeTab) { fetchProducts(); }
    if (activeTab === 'production') fetchProductions();
    if (activeTab === 'lab-reports') fetchLabReports();
    if (activeTab === 'batch-timeline') fetchBatches();
  }, [activeTab]);

  usePageCallbacks({
    onNewEntry: () => {
      if (activeTab === 'production') openProdDialog();
      if (activeTab === 'lab-reports') openLabDialog();
    },
  });

  const fetchProducts = async () => {
    try { const r = await getProducts(); setProducts(r.data); }
    catch (err) { console.error('Failed to load products:', err); }
  };

  // ==================== PRODUCTION ====================
  const fetchProductions = async () => {
    setProdLoading(true);
    try { const r = await getProductions(); setProductions(r.data); }
    catch (err) { console.error('Failed to load productions:', err); toast.error('Failed to load productions'); }
    finally { setProdLoading(false); }
  };

  const openProdDialog = () => {
    setProdForm({ date: getToday(), input_items: [_emptyInput()], output_items: [_emptyOutput()], remarks: '' });
    setProdError(''); setProdDialog(true);
  };

  const fetchBatchesForProduct = async (productId, idx) => {
    try {
      const r = await getProductStockDetail(productId);
      setAvailableBatches(prev => ({ ...prev, [idx]: r.data.batches.filter(b => b.quantity_kg > 0) }));
    } catch (err) { console.error('Failed to load batches:', err); }
  };

  const updateInput = (idx, field, value) => {
    setProdForm(prev => {
      const items = [...prev.input_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, input_items: items };
    });
    if (field === 'product_id' && value) fetchBatchesForProduct(value, idx);
  };

  const updateOutput = (idx, field, value) => {
    setProdForm(prev => {
      const items = [...prev.output_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, output_items: items };
    });
  };

  const addInput = () => setProdForm(prev => ({ ...prev, input_items: [...prev.input_items, _emptyInput()] }));
  const removeInput = (idx) => setProdForm(prev => ({ ...prev, input_items: prev.input_items.filter((_, i) => i !== idx) }));
  const addOutput = () => setProdForm(prev => ({ ...prev, output_items: [...prev.output_items, _emptyOutput()] }));
  const removeOutput = (idx) => setProdForm(prev => ({ ...prev, output_items: prev.output_items.filter((_, i) => i !== idx) }));

  // Custom Enter key handler for production form
  const handleProdFormKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const tag = e.target.tagName.toLowerCase();
    if (tag === 'button' || e.target.type === 'submit') return;
    if (e.target.getAttribute('role') === 'combobox') return;
    if (e.target.closest('[role="listbox"]')) return;
    const form = e.target.closest('form');
    if (!form) return;
    const openDropdown = form.querySelector('[aria-expanded="true"]');
    if (openDropdown) return;

    const inputQtyIdx = e.target.getAttribute('data-input-qty');
    const outputQtyIdx = e.target.getAttribute('data-output-qty');
    const outputBrandIdx = e.target.getAttribute('data-output-brand');

    if (inputQtyIdx !== null) {
      e.preventDefault();
      const idx = parseInt(inputQtyIdx);
      const lastIdx = prodForm.input_items.length - 1;
      if (idx === lastIdx) {
        // Last input line: if product is selected, add new line; otherwise move to output section
        if (prodForm.input_items[idx].product_id) {
          addInput();
          // Focus new product field after render
          setTimeout(() => {
            const containers = form.querySelectorAll('[data-input-product-wrap]');
            const last = containers[containers.length - 1];
            if (last) { const btn = last.querySelector('button'); if (btn) btn.click(); }
          }, 100);
        } else {
          // Move to first output product
          const firstOutput = form.querySelector('[data-output-product-wrap="0"]');
          if (firstOutput) { const btn = firstOutput.querySelector('button'); if (btn) btn.click(); }
        }
      } else {
        // Not last line: move to next line's product selector
        const nextContainer = form.querySelector(`[data-input-product-wrap="${idx + 1}"]`);
        if (nextContainer) { const btn = nextContainer.querySelector('button'); if (btn) btn.click(); }
      }
      return;
    }

    if (outputQtyIdx !== null) {
      e.preventDefault();
      const idx = parseInt(outputQtyIdx);
      // Move to brand field of same line
      const brandInput = form.querySelector(`[data-output-brand="${idx}"]`);
      if (brandInput) { brandInput.focus(); brandInput.select(); }
      return;
    }

    if (outputBrandIdx !== null) {
      e.preventDefault();
      const idx = parseInt(outputBrandIdx);
      const lastIdx = prodForm.output_items.length - 1;
      if (idx === lastIdx) {
        // Last output: if product selected, add new line; otherwise move to remarks
        if (prodForm.output_items[idx].product_id) {
          addOutput();
          setTimeout(() => {
            const containers = form.querySelectorAll('[data-output-product-wrap]');
            const last = containers[containers.length - 1];
            if (last) { const btn = last.querySelector('button'); if (btn) btn.click(); }
          }, 100);
        } else {
          const remarksInput = form.querySelector('[data-prod-remarks]');
          if (remarksInput) { remarksInput.focus(); }
        }
      } else {
        const nextContainer = form.querySelector(`[data-output-product-wrap="${idx + 1}"]`);
        if (nextContainer) { const btn = nextContainer.querySelector('button'); if (btn) btn.click(); }
      }
      return;
    }

    // For remarks field: Enter submits
    if (e.target.hasAttribute('data-prod-remarks')) {
      e.preventDefault();
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.click();
      return;
    }

    // Default: move to next field
    const allInputs = Array.from(form.querySelectorAll('input:not([type="hidden"]):not([disabled])')).filter(el => el.offsetParent !== null);
    const currentIndex = allInputs.indexOf(e.target);
    if (currentIndex >= 0 && currentIndex < allInputs.length - 1) {
      e.preventDefault();
      allInputs[currentIndex + 1].focus();
      if (['text', 'number'].includes(allInputs[currentIndex + 1].type)) allInputs[currentIndex + 1].select();
    }
  };

  const handleCreateProduction = async (e) => {
    e.preventDefault(); setProdError(''); setProdSubmitting(true);
    try {
      const payload = {
        date: prodForm.date,
        input_items: prodForm.input_items.filter(i => i.product_id && i.batch_id && i.quantity_kg).map(i => ({ ...i, quantity_kg: parseFloat(i.quantity_kg) })),
        output_items: prodForm.output_items.filter(o => o.product_id && o.quantity_kg).map(o => ({ ...o, quantity_kg: parseFloat(o.quantity_kg) })),
        remarks: prodForm.remarks,
      };
      if (payload.input_items.length === 0) { setProdError('At least one input item required'); setProdSubmitting(false); return; }
      if (payload.output_items.length === 0) { setProdError('At least one output item required'); setProdSubmitting(false); return; }
      await createProduction(payload);
      toast.success('Production entry created');
      setProdDialog(false); fetchProductions();
    } catch (err) { setProdError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setProdSubmitting(false); }
  };

  const handleDeleteProduction = async (p) => {
    if (!window.confirm(`Delete ${p.production_number}? This will reverse inventory changes.`)) return;
    try { await deleteProduction(p.id); toast.success('Deleted & reversed'); fetchProductions(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const productOptions = products.map(p => ({ value: p.id, label: p.name }));

  // ==================== LAB REPORTS ====================
  const fetchLabReports = async () => {
    setLabLoading(true);
    try { const r = await getLabReports(); setLabReports(r.data); }
    catch (err) { console.error('Failed to load lab reports:', err); toast.error('Failed to load lab reports'); }
    finally { setLabLoading(false); }
  };

  const openLabDialog = (report) => {
    if (report) {
      setEditingLab(report);
      setLabForm({ production_id: report.production_id, batch_id: report.batch_id, date: report.date, temperature: report.temperature || '', time: report.time || '', ratio: report.ratio || '', viscosity: report.viscosity || '', composition: report.composition || '', remarks: report.remarks || '' });
    } else {
      setEditingLab(null);
      setLabForm({ production_id: '', batch_id: '', date: getToday(), temperature: '', time: '', ratio: '', viscosity: '', composition: '', remarks: '' });
    }
    setLabError(''); setLabDialog(true);
  };

  const handleLabSubmit = async (e) => {
    e.preventDefault(); setLabError(''); setLabSubmitting(true);
    try {
      if (editingLab) {
        await updateLabReport(editingLab.id, labForm);
        toast.success('Lab report updated');
      } else {
        if (!labForm.production_id || !labForm.batch_id) { setLabError('Select a production entry and batch'); setLabSubmitting(false); return; }
        await createLabReport(labForm);
        toast.success('Lab report created');
      }
      setLabDialog(false); fetchLabReports();
    } catch (err) { setLabError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLabSubmitting(false); }
  };

  const handleDeleteLab = async (r) => {
    if (!window.confirm('Delete this lab report?')) return;
    try { await deleteLabReport(r.id); toast.success('Deleted'); fetchLabReports(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // Production options for lab report form
  const productionOptions = productions.filter(p => p.output_batch_ids?.length > 0).map(p => ({ value: p.id, label: `${p.production_number} (${formatDate(p.date)})` }));
  const selectedProdForLab = productions.find(p => p.id === labForm.production_id);
  const batchOptionsForLab = (selectedProdForLab?.output_batch_ids || []).map((bid, idx) => {
    const outItem = selectedProdForLab?.output_items?.[idx];
    return { value: bid, label: outItem ? `${outItem.product_name || 'Output'} - ${outItem.quantity_kg} KG` : bid };
  });

  // ==================== BATCH TIMELINE ====================
  const fetchBatches = async () => {
    try { const r = await getBatchTimelineProducts(); setAllBatches(r.data); }
    catch (err) { console.error('Failed to load batches:', err); }
  };

  const loadTimeline = async (batchId) => {
    setSelectedBatchId(batchId);
    if (!batchId) { setTimeline(null); return; }
    setTimelineLoading(true); setExpandedTimelineIdx(null);
    try { const r = await getBatchTimeline(batchId); setTimeline(r.data); }
    catch (err) { console.error('Failed to load timeline:', err); toast.error('Failed to load timeline'); }
    finally { setTimelineLoading(false); }
  };

  const batchOptions = allBatches.map(b => ({ value: b.id, label: `${b.product_name} - ${b.batch_number} (${b.quantity_kg.toLocaleString()} KG)` }));

  const SECTION_DEFS = [
    { key: '1', value: 'production', label: 'Production', icon: Factory },
    { key: '2', value: 'lab-reports', label: 'Lab Reports', icon: FlaskConical },
    { key: '3', value: 'batch-timeline', label: 'Batch Timeline', icon: GitBranch },
  ];

  const getHeaderActions = () => {
    if (activeTab === 'production') return (
      <div className="flex gap-2">
        <ExportToolbar endpoint="/exports/productions" fileBaseName="production-register" testId="export-production" />
        <Button size="sm" onClick={openProdDialog} data-testid="add-production-btn"><Plus className="h-4 w-4 mr-1" />New Production</Button>
      </div>
    );
    if (activeTab === 'lab-reports') return (
      <div className="flex gap-2">
        <ExportToolbar endpoint="/exports/lab-reports" fileBaseName="lab-reports" testId="export-lab" />
        <Button size="sm" onClick={() => openLabDialog()} data-testid="add-lab-btn"><Plus className="h-4 w-4 mr-1" />New Lab Report</Button>
      </div>
    );
    return null;
  };

  return (
    <SectionTiles sections={SECTION_DEFS} activeSection={activeTab} onSelect={setActiveTab} pageTitle="Production" pageDescription="Production, lab reports, and batch tracking" headerActions={getHeaderActions()}>

      {/* ==================== PRODUCTION TAB ==================== */}
      {activeTab === 'production' && (prodLoading ? <Skeleton className="h-64 w-full" /> :
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Production #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Input (KG)</TableHead>
                <TableHead className="text-right">Output (KG)</TableHead>
                <TableHead className="hidden sm:table-cell">Lab</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {productions.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No production entries yet</TableCell></TableRow>
                ) : productions.map(p => (
                  <React.Fragment key={p.id}>
                    <TableRow className={`cursor-pointer hover:bg-muted/50 ${expandedProdId === p.id ? 'bg-muted/30' : ''}`} onClick={() => setExpandedProdId(expandedProdId === p.id ? null : p.id)} data-testid={`prod-row-${p.id}`}>
                      <TableCell className="px-2">{expandedProdId === p.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</TableCell>
                      <TableCell className="font-mono font-medium text-sm">{p.production_number}</TableCell>
                      <TableCell className="text-sm">{formatDate(p.date)}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-orange-600">{p.total_input_kg?.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-green-600">{p.total_output_kg?.toLocaleString()}</TableCell>
                      <TableCell className="hidden sm:table-cell">{p.has_lab_report ? <Badge variant="outline" className="text-[10px] text-green-600">Yes</Badge> : <span className="text-xs text-muted-foreground">-</span>}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteProduction(p)}><Trash2 className="h-3 w-3" /></Button></TableCell>
                    </TableRow>
                    {expandedProdId === p.id && (
                      <TableRow className="bg-muted/10"><TableCell colSpan={7} className="p-0">
                        <div className="px-4 py-3 space-y-3">
                          {p.remarks && <div className="text-xs text-muted-foreground bg-background border rounded px-2 py-1.5"><span className="font-medium text-foreground">Remarks:</span> {p.remarks}</div>}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-1">Consumption (Input)</p>
                              {p.input_items?.map((item, idx) => (
                                <div key={idx} className="text-xs bg-background border rounded px-2 py-1.5 mb-1 flex justify-between">
                                  <span>{item.product_name} <span className="text-muted-foreground">({item.batch_number})</span></span>
                                  <span className="font-mono">{item.quantity_kg.toLocaleString()} KG</span>
                                </div>
                              ))}
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Production (Output)</p>
                              {p.output_items?.map((item, idx) => (
                                <div key={idx} className="text-xs bg-background border rounded px-2 py-1.5 mb-1 flex justify-between">
                                  <span>{item.product_name} {item.brand && <span className="text-muted-foreground">({item.brand})</span>}</span>
                                  <span className="font-mono">{item.quantity_kg.toLocaleString()} KG</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </TableCell></TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ==================== LAB REPORTS TAB ==================== */}
      {activeTab === 'lab-reports' && (labLoading ? <Skeleton className="h-64 w-full" /> :
        <div className="space-y-3">
          {labReports.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">No lab reports yet. Create a production entry first, then add lab reports.</CardContent></Card>
          ) : labReports.map(r => (
            <Card key={r.id} data-testid={`lab-row-${r.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between mb-2 cursor-pointer" onClick={() => setExpandedLabId(expandedLabId === r.id ? null : r.id)}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium">{r.production_number}</span>
                      <Badge variant="outline" className="text-[10px]">{r.output_product_name} - {r.batch_number}</Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(r.date)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openLabDialog(r); }}><FileText className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteLab(r); }}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
                {(expandedLabId === r.id || true) && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                      {r.temperature && <div className="bg-muted/50 rounded px-2 py-1"><span className="text-muted-foreground">Temp:</span> <span className="font-medium">{r.temperature}</span></div>}
                      {r.time && <div className="bg-muted/50 rounded px-2 py-1"><span className="text-muted-foreground">Time:</span> <span className="font-medium">{r.time}</span></div>}
                      {r.ratio && <div className="bg-muted/50 rounded px-2 py-1"><span className="text-muted-foreground">Ratio:</span> <span className="font-medium">{r.ratio}</span></div>}
                      {r.viscosity && <div className="bg-muted/50 rounded px-2 py-1"><span className="text-muted-foreground">Viscosity:</span> <span className="font-medium">{r.viscosity}</span></div>}
                      {r.composition && <div className="bg-muted/50 rounded px-2 py-1"><span className="text-muted-foreground">Composition:</span> <span className="font-medium">{r.composition}</span></div>}
                    </div>
                    {r.remarks && <div className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Remarks:</span> {r.remarks}</div>}
                    {r.input_products?.length > 0 && (
                      <div className="text-xs"><span className="text-muted-foreground">Inputs:</span> {r.input_products.map(i => `${i.product_name} (${i.quantity_kg} KG)`).join(', ')}</div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ==================== BATCH TIMELINE TAB ==================== */}
      {activeTab === 'batch-timeline' && (
        <div className="space-y-4">
          <div className="max-w-md">
            <Label className="mb-2 block">Select Batch</Label>
            <SearchableSelect options={batchOptions} value={selectedBatchId} onChange={loadTimeline} placeholder="Search by product or batch number..." data-testid="timeline-batch-select" />
          </div>

          {timelineLoading && <Skeleton className="h-64 w-full" />}

          {timeline && !timelineLoading && (
            <div className="space-y-4">
              {/* Batch info header */}
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{timeline.batch.product_name}</h3>
                      <p className="text-xs text-muted-foreground">Batch: <span className="font-mono">{timeline.batch.batch_number}</span> {timeline.batch.brand && `(${timeline.batch.brand})`}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold font-mono">{timeline.batch.current_quantity_kg.toLocaleString()} <span className="text-xs font-normal">KG</span></p>
                      {timeline.batch.original_quantity_kg !== timeline.batch.current_quantity_kg && (
                        <p className="text-xs text-muted-foreground">Original: <span className="font-mono">{timeline.batch.original_quantity_kg?.toLocaleString()}</span> KG</p>
                      )}
                      <Badge variant="outline" className="text-[10px] mt-0.5">{timeline.batch.source_type.replace('_', ' ')}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Timeline table */}
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="text-xs w-24">Date</TableHead>
                      <TableHead className="text-xs">Transaction</TableHead>
                      <TableHead className="text-right text-xs w-28">Qty (KG)</TableHead>
                      <TableHead className="text-right text-xs w-28">Balance (KG)</TableHead>
                      <TableHead className="w-8"></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {timeline.timeline.map((entry, idx) => (
                        <React.Fragment key={`${entry.type}-${entry.date}-${entry.reference_id || idx}`}>
                          <TableRow
                            className={`cursor-pointer hover:bg-muted/50 ${expandedTimelineIdx === idx ? 'bg-muted/30' : ''}`}
                            onClick={() => setExpandedTimelineIdx(expandedTimelineIdx === idx ? null : idx)}
                            data-testid={`timeline-entry-${idx}`}
                          >
                            <TableCell className="text-xs font-mono">{formatDate(entry.date)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className={`text-[10px] shrink-0 ${TYPE_COLORS[entry.type] || ''}`}>
                                  {entry.type === 'batch_created' && <ArrowDown className="inline h-3 w-3 mr-0.5" />}
                                  {entry.type === 'production_consumed' && <Minus className="inline h-3 w-3 mr-0.5" />}
                                  {entry.type === 'sales_dispatch' && <ArrowUp className="inline h-3 w-3 mr-0.5" />}
                                  {entry.type === 'lab_report' && <FlaskConical className="inline h-3 w-3 mr-0.5" />}
                                  {entry.label}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell className={`text-right font-mono text-sm font-medium ${entry.quantity_kg > 0 ? 'text-green-600' : entry.quantity_kg < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                              {entry.quantity_kg !== 0 ? `${entry.quantity_kg > 0 ? '+' : ''}${entry.quantity_kg.toLocaleString()}` : '-'}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm font-semibold">{entry.running_balance?.toLocaleString()}</TableCell>
                            <TableCell className="px-1">{expandedTimelineIdx === idx ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}</TableCell>
                          </TableRow>
                          {expandedTimelineIdx === idx && entry.details && (
                            <TableRow className="bg-muted/10"><TableCell colSpan={5} className="p-0">
                              <div className="px-4 py-2.5 space-y-2">
                                {/* Structured detail view based on type */}
                                {entry.type === 'batch_created' && (
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                                    {entry.details.batch_number && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Batch Number</span><span className="font-mono font-medium">{entry.details.batch_number}</span></div>}
                                    {entry.details.brand && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Brand</span><span className="font-medium">{entry.details.brand}</span></div>}
                                    {entry.details.source_type && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Source</span><span className="font-medium capitalize">{entry.details.source_type.replace('_', ' ')}</span></div>}
                                    {entry.details.po_number && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">PO Number</span><span className="font-mono font-medium">{entry.details.po_number}</span></div>}
                                    {entry.details.invoice_number && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Invoice</span><span className="font-mono font-medium">{entry.details.invoice_number}</span></div>}
                                    {entry.details.vehicle_number && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Vehicle</span><span className="font-medium">{entry.details.vehicle_number}</span></div>}
                                    {entry.details.supplier_name && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Supplier</span><span className="font-medium">{entry.details.supplier_name}</span></div>}
                                    {entry.details.grand_total > 0 && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Amount</span><span className="font-mono font-medium">{formatCurrency(entry.details.grand_total)}</span></div>}
                                    {entry.details.production_number && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Production</span><span className="font-mono font-medium">{entry.details.production_number}</span></div>}
                                  </div>
                                )}
                                {entry.type === 'production_consumed' && (
                                  <div className="space-y-2 text-xs">
                                    <div className="flex items-center gap-2">
                                      <span className="text-muted-foreground">Production:</span>
                                      <span className="font-mono font-medium">{entry.details.production_number}</span>
                                    </div>
                                    {entry.details.output_items?.length > 0 && (
                                      <div><span className="text-muted-foreground">Output:</span> {entry.details.output_items.map(o => `${o.product_name} (${o.quantity_kg.toLocaleString()} KG)`).join(', ')}</div>
                                    )}
                                    {entry.details.remarks && <div><span className="text-muted-foreground">Remarks:</span> {entry.details.remarks}</div>}
                                  </div>
                                )}
                                {entry.type === 'sales_dispatch' && (
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                                    {entry.details.so_number && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">SO Number</span><span className="font-mono font-medium">{entry.details.so_number}</span></div>}
                                    {entry.details.invoice_number && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Invoice</span><span className="font-mono font-medium">{entry.details.invoice_number}</span></div>}
                                    {entry.details.customer_name && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Customer</span><span className="font-medium">{entry.details.customer_name}</span></div>}
                                    {entry.details.vehicle_number && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Vehicle</span><span className="font-medium">{entry.details.vehicle_number}</span></div>}
                                    {entry.details.brand && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Brand</span><span className="font-medium">{entry.details.brand}</span></div>}
                                    {entry.details.rate > 0 && <div className="bg-background rounded border px-2 py-1.5"><span className="text-muted-foreground block">Rate</span><span className="font-mono font-medium">{formatCurrency(entry.details.rate)}</span></div>}
                                  </div>
                                )}
                                {entry.type === 'lab_report' && (
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                                    {entry.details.temperature && <div className="bg-muted/50 rounded px-2 py-1.5"><span className="text-muted-foreground">Temperature:</span> <span className="font-medium">{entry.details.temperature}</span></div>}
                                    {entry.details.time && <div className="bg-muted/50 rounded px-2 py-1.5"><span className="text-muted-foreground">Time:</span> <span className="font-medium">{entry.details.time}</span></div>}
                                    {entry.details.ratio && <div className="bg-muted/50 rounded px-2 py-1.5"><span className="text-muted-foreground">Ratio:</span> <span className="font-medium">{entry.details.ratio}</span></div>}
                                    {entry.details.viscosity && <div className="bg-muted/50 rounded px-2 py-1.5"><span className="text-muted-foreground">Viscosity:</span> <span className="font-medium">{entry.details.viscosity}</span></div>}
                                    {entry.details.composition && <div className="bg-muted/50 rounded px-2 py-1.5"><span className="text-muted-foreground">Composition:</span> <span className="font-medium">{entry.details.composition}</span></div>}
                                    {entry.details.remarks && <div className="bg-muted/50 rounded px-2 py-1.5 col-span-2"><span className="text-muted-foreground">Remarks:</span> <span className="font-medium">{entry.details.remarks}</span></div>}
                                  </div>
                                )}
                              </div>
                            </TableCell></TableRow>
                          )}
                        </React.Fragment>
                      ))}
                      {/* Closing balance row */}
                      <TableRow className="border-t-2 font-semibold">
                        <TableCell colSpan={2} className="text-xs">Closing Balance</TableCell>
                        <TableCell className="text-right"></TableCell>
                        <TableCell className="text-right font-mono text-sm">{timeline.batch.current_quantity_kg.toLocaleString()}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* ==================== PRODUCTION DIALOG ==================== */}
      <Dialog open={prodDialog} onOpenChange={setProdDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Production Entry</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateProduction} onKeyDown={handleProdFormKeyDown} className="space-y-4">
            {prodError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{prodError}</AlertDescription></Alert>}
            <div className="space-y-2"><Label>Date</Label><DateInput value={prodForm.date} onChange={e => setProdForm(p => ({...p, date: e.target.value}))} required /></div>

            {/* Input Items (Consumption) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold text-orange-600">Consumption (Input)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addInput}><Plus className="h-3 w-3 mr-1" />Add Input</Button>
              </div>
              {prodForm.input_items.map((item, idx) => (
                <div key={item._uid || idx} className="grid grid-cols-[1fr_1fr_0.8fr_auto] gap-2 items-end">
                  <div className="space-y-1" data-input-product-wrap={idx}><Label className="text-xs">Product</Label>
                    <SearchableSelect options={productOptions} value={item.product_id} onChange={v => updateInput(idx, 'product_id', v)} placeholder="Product" />
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Batch</Label>
                    <SearchableSelect options={(availableBatches[idx] || []).map(b => ({ value: b.id, label: `${b.batch_number} (${b.quantity_kg.toLocaleString()} KG)` }))} value={item.batch_id} onChange={v => updateInput(idx, 'batch_id', v)} placeholder="Batch" />
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Qty (KG)</Label>
                    <Input type="number" step="0.01" min="0" value={item.quantity_kg} onChange={e => updateInput(idx, 'quantity_kg', e.target.value)} placeholder="KG" data-input-qty={idx} />
                  </div>
                  {prodForm.input_items.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeInput(idx)}><Trash2 className="h-3 w-3" /></Button>}
                </div>
              ))}
            </div>

            {/* Output Items (Production) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold text-green-600">Production (Output)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addOutput}><Plus className="h-3 w-3 mr-1" />Add Output</Button>
              </div>
              {prodForm.output_items.map((item, idx) => (
                <div key={item._uid || idx} className="grid grid-cols-[1fr_0.8fr_0.8fr_auto] gap-2 items-end">
                  <div className="space-y-1" data-output-product-wrap={idx}><Label className="text-xs">Product</Label>
                    <SearchableSelect options={productOptions} value={item.product_id} onChange={v => updateOutput(idx, 'product_id', v)} placeholder="Product" />
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Qty (KG)</Label>
                    <Input type="number" step="0.01" min="0" value={item.quantity_kg} onChange={e => updateOutput(idx, 'quantity_kg', e.target.value)} placeholder="KG" data-output-qty={idx} />
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Brand</Label>
                    <Input value={item.brand} onChange={e => updateOutput(idx, 'brand', e.target.value)} placeholder="Brand" data-output-brand={idx} />
                  </div>
                  {prodForm.output_items.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeOutput(idx)}><Trash2 className="h-3 w-3" /></Button>}
                </div>
              ))}
            </div>

            <div className="space-y-2"><Label>Remarks</Label><Input value={prodForm.remarks} onChange={e => setProdForm(p => ({...p, remarks: e.target.value}))} placeholder="Optional" data-prod-remarks="" /></div>
            <DialogFooter><FormShortcutHints /><Button type="button" variant="outline" onClick={() => setProdDialog(false)}>Cancel</Button><Button type="submit" disabled={prodSubmitting} data-testid="prod-submit">{prodSubmitting ? 'Creating...' : 'Create Production'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ==================== LAB REPORT DIALOG ==================== */}
      <Dialog open={labDialog} onOpenChange={setLabDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingLab ? 'Edit Lab Report' : 'New Lab Report'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleLabSubmit} className="space-y-4">
            {labError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{labError}</AlertDescription></Alert>}
            {!editingLab && (
              <>
                <div className="space-y-2"><Label>Production Entry</Label>
                  <SearchableSelect options={productionOptions} value={labForm.production_id} onChange={v => setLabForm(p => ({...p, production_id: v, batch_id: ''}))} placeholder="Select production" />
                </div>
                {labForm.production_id && (
                  <div className="space-y-2"><Label>Output Batch</Label>
                    <SearchableSelect options={batchOptionsForLab} value={labForm.batch_id} onChange={v => setLabForm(p => ({...p, batch_id: v}))} placeholder="Select batch" />
                  </div>
                )}
              </>
            )}
            <div className="space-y-2"><Label>Date</Label><DateInput value={labForm.date} onChange={e => setLabForm(p => ({...p, date: e.target.value}))} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Temperature</Label><Input value={labForm.temperature} onChange={e => setLabForm(p => ({...p, temperature: e.target.value}))} placeholder="e.g. 85 C" /></div>
              <div className="space-y-2"><Label>Time</Label><Input value={labForm.time} onChange={e => setLabForm(p => ({...p, time: e.target.value}))} placeholder="e.g. 2 hours" /></div>
              <div className="space-y-2"><Label>Ratio</Label><Input value={labForm.ratio} onChange={e => setLabForm(p => ({...p, ratio: e.target.value}))} placeholder="e.g. 1:2.5" /></div>
              <div className="space-y-2"><Label>Viscosity</Label><Input value={labForm.viscosity} onChange={e => setLabForm(p => ({...p, viscosity: e.target.value}))} placeholder="e.g. 350 cP" /></div>
            </div>
            <div className="space-y-2"><Label>Composition</Label><Input value={labForm.composition} onChange={e => setLabForm(p => ({...p, composition: e.target.value}))} placeholder="e.g. 80% starch, 20% water" /></div>
            <div className="space-y-2"><Label>Remarks</Label><Input value={labForm.remarks} onChange={e => setLabForm(p => ({...p, remarks: e.target.value}))} placeholder="Additional notes" /></div>
            <DialogFooter><FormShortcutHints /><Button type="button" variant="outline" onClick={() => setLabDialog(false)}>Cancel</Button><Button type="submit" disabled={labSubmitting} data-testid="lab-submit">{labSubmitting ? 'Saving...' : editingLab ? 'Update' : 'Create'}</Button></DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

    </SectionTiles>
  );
}
